import React, { useState, useEffect } from "react";
import {
  Container,
  Table,
  Title,
  Group,
  Text,
  MultiSelect,
  Paper,
  Stack,
  Button,
  Collapse,
  Select,
  Grid,
  SimpleGrid,
  Card,
  Menu,
  ActionIcon,
} from "@mantine/core";
import {
  IconFilter,
  IconChevronUp,
  IconChevronDown,
  IconArrowLeft,
  IconArrowRight,
} from "@tabler/icons-react";
import { useDisclosure } from "@mantine/hooks";
import {
  collection,
  getDocs,
  query,
  where,
  limit,
  startAfter,
  startAt,
  QueryDocumentSnapshot,
} from "firebase/firestore";
import { db } from "../../firebase/FirebaseCredential";
import { useNavigate } from "react-router-dom";
import { BenchTable } from "../pages/UI";
import BenchPaper from "../pages/UI/BenchPaper";
import { DefaultSystemPermission } from "../PermissionComponent/DefaultSystemPermission";
import BenchLoader from "../UIComponent/BenchLoader/BenchLoader";
import { FcExpand } from "react-icons/fc";

interface WorkoutPlan {
  id: string;
  goal: string;
  level: string;
  location: string;
  num_of_workouts: number;
}

interface Filters {
  goal?: string[];
  level?: string[];
  location?: string[];
}

const DefaultRoutinePlanMainTable = () => {
  // Filters
  const [tempFilters, setTempFilters] = useState<Filters>({});
  const [activeFilters, setActiveFilters] = useState<Filters>({});
  const [filterOpen, toggleFilters] = useDisclosure(true);
  const [loading, setLoading] = useState(false);

  // Pagination
  const [tableData, setTableData] = useState<WorkoutPlan[]>([]);
  const [itemsPerPage, setItemsPerPage] = useState<number>(5);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageCursors, setPageCursors] = useState<
    QueryDocumentSnapshot<WorkoutPlan>[]
  >([]);

  // Fetch first page initially
  useEffect(() => {
    fetchPage("first");
  }, []);
  const navigate = useNavigate();

  const [hasNextPage, setHasNextPage] = useState(false);

  // Fetch paginated data from Firestore
  const fetchPage = async (
    direction: "next" | "prev" | "first" = "first",
    limitCount?: number,
    filters?: Filters
  ) => {
    try {
      setLoading(true);
      const perPage = limitCount || itemsPerPage;
      const appliedFilters = filters || activeFilters;

      // We always fetch perPage + 1 to check if there is a next page
      let q = query(collection(db, "systemroutines"), limit(perPage + 1));

      // Apply filters
      if (appliedFilters.goal?.length)
        q = query(q, where("goal", "in", appliedFilters.goal));
      if (appliedFilters.level?.length)
        q = query(q, where("level", "in", appliedFilters.level));
      if (appliedFilters.location?.length)
        q = query(q, where("location", "in", appliedFilters.location));

      // Handle pagination
      if (direction === "next" && pageCursors.length > 0) {
        const lastDocOfCurrentPage = pageCursors[pageCursors.length - 1];
        q = query(q, startAfter(lastDocOfCurrentPage));
      } else if (direction === "prev" && currentPage > 1) {
        // To go back, we need the cursor that was used to start the page BEFORE the current one
        // If we are on page 3, cursors has [end of p1, end of p2]. 
        // To get p2, we need startAfter(end of p1), which is cursors[currentPage - 3]
        const targetCursorIndex = currentPage - 3;
        if (targetCursorIndex >= 0) {
          q = query(q, startAfter(pageCursors[targetCursorIndex]));
        }
        // If it's the first page (currentPage was 2), we don't add startAfter
      }

      const snap = await getDocs(q);

      if (!snap.empty) {
        const docs = snap.docs;
        const hasNext = docs.length > perPage;
        setHasNextPage(hasNext);

        // Actual data is only up to perPage
        const visibleDocs = hasNext ? docs.slice(0, perPage) : docs;

        setTableData(
          visibleDocs.map((doc) => ({
            id: doc.id,
            ...(doc.data() as Omit<WorkoutPlan, "id">),
          }))
        );

        if (direction === "next") {
          setPageCursors((prev) => [...prev, visibleDocs[visibleDocs.length - 1] as QueryDocumentSnapshot<WorkoutPlan>]);
          setCurrentPage((prev) => prev + 1);
        } else if (direction === "prev") {
          setPageCursors((prev) => prev.slice(0, prev.length - 1));
          setCurrentPage((prev) => prev - 1);
        } else {
          // "first"
          setPageCursors([visibleDocs[visibleDocs.length - 1] as QueryDocumentSnapshot<WorkoutPlan>]);
          setCurrentPage(1);
        }
      } else {
        if (direction === "first") {
          setTableData([]);
          setHasNextPage(false);
          setPageCursors([]);
          setCurrentPage(1);
        }
      }
    } catch (err) {
      console.error("Error fetching page:", err);
    } finally {
      setLoading(false);
    }
  };

  // Apply Filter button
  const handleApplyFilters = () => {
    setActiveFilters(tempFilters);
    setPageCursors([]);
    fetchPage("first", itemsPerPage, tempFilters);
  };

  // Clear Filter button
  const handleClearFilters = () => {
    setTempFilters({});
    setActiveFilters({});
    setPageCursors([]);
    fetchPage("first", itemsPerPage, {});
  };

  // Rows per page change
  const handleRowsChange = (val: string) => {
    const num = Number(val);
    setItemsPerPage(num);
    setPageCursors([]);
    fetchPage("first", num);
  };
  const handleRotineNavigate = (routineId: string) => {
    navigate(`/defaultroutine/${routineId}`);
  };

  return (
    <Container fluid p={0}>

      {/* Header */}
      <Group position="apart" mb="sm">
        {/* <Group>{/* <Title order={3}>Default Routines</Title> */}</Group>
      {/* <Button
          leftIcon={<IconFilter size={16} />}
          rightIcon={
            filterOpen ? (
              <IconChevronUp size={16} />
            ) : (
              <IconChevronDown size={16} />
            )
          }
          onClick={toggleFilters}
          variant="light"
        >
          Filters
        </Button>
      </Group> */}


      <Card my="lg" withBorder radius="lg">
        <SimpleGrid
          breakpoints={[
            { minWidth: "xs", cols: 1 },
            { minWidth: "md", cols: 4 },
          ]}
        >
          <MultiSelect
            label="Goals"
            placeholder="Select goals"
            value={tempFilters.goal || []}
            onChange={(val: string[]) =>
              setTempFilters((prev) => ({ ...prev, goal: val }))
            }
            data={[
              "Maintain Fitness",
              "Strength",
              "Weight Loss",
              "Muscle Gain",
              "Endurance",
            ]}
            searchable
            clearable
            withinPortal
          />

          <MultiSelect
            label="Levels"
            placeholder="Select levels"
            value={tempFilters.level || []}
            onChange={(val: string[]) =>
              setTempFilters((prev) => ({ ...prev, level: val }))
            }
            data={["Beginner", "Intermediate", "Advanced"]}
            searchable
            clearable
            withinPortal
          />

          <MultiSelect
            label="Locations"
            placeholder="Select locations"
            value={tempFilters.location || []}
            onChange={(val: string[]) =>
              setTempFilters((prev) => ({ ...prev, location: val }))
            }
            data={["Gym", "Home", "Travel"]}
            searchable
            clearable
            withinPortal
          />

          <Group mt={24} align="flex-end" spacing="xs">
            <Button
              onClick={handleClearFilters}
              variant="outline"
              sx={{ flex: 1 }}
            >
              Clear
            </Button>
            <Button
              onClick={handleApplyFilters}
              variant="filled"
              sx={{ flex: 2 }}
            >
              Apply Filters
            </Button>
          </Group>
        </SimpleGrid>
      </Card>

      {/* Table */}
      <BenchTable>
        <thead>
          <tr>
            <th>Goal</th>
            <th>Level</th>
            <th>Location</th>
            <th>Workout Count</th>
            <th>Action</th>
          </tr>
        </thead>
        <tbody>
          {tableData.length > 0 ? (
            tableData.map((plan) => (
              <tr onClick={() => handleRotineNavigate(plan.id)} key={plan.id}>
                <td>{plan.goal}</td>
                <td>{plan.level}</td>
                <td>{plan.location}</td>
                <td>{plan.num_of_workouts}</td>
                <td onClick={(e) => e.stopPropagation()} style={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                }}>
                  <Menu withinPortal withArrow position="bottom-end">
                    <Menu.Target>
                      <ActionIcon variant="outline">
                        <FcExpand />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item 
                        onClick={() => navigate('/routineplancreate', { state: { templateToModify: plan } })}
                      >
                        Modify Template
                      </Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan={5}
                style={{ textAlign: "center", fontWeight: "bold" }}
              >
                No Data Available
              </td>
            </tr>
          )}
        </tbody>
      </BenchTable>

      {/* Pagination */}
      <Group position="apart" mt="md">
        <div style={{ display: "flex", alignItems: "center" }}>
          <Text>Rows per page:</Text>
          <Select
            style={{ width: 80, marginLeft: 5 }}
            value={itemsPerPage.toString()}
            onChange={handleRowsChange}
            data={["5", "10", "25", "30"].map((v) => ({
              value: v,
              label: v,
            }))}
          />
        </div>

        <Group my="md">
          <Button
            leftIcon={<IconArrowLeft size={14} />}
            disabled={currentPage === 1}
            onClick={() => fetchPage("prev")}
          >
            Prev
          </Button>
          <Text>Page {currentPage}</Text>
          <Button
            rightIcon={<IconArrowRight size={14} />}
            onClick={() => fetchPage("next")}
            disabled={!hasNextPage}
          >
            Next
          </Button>
        </Group>
      </Group>

      <BenchLoader isLoading={loading} />
    </Container>
  );
};

export default DefaultRoutinePlanMainTable;
